import React, { PropsWithChildren } from 'react';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { Variant as TypographyVariant } from '@material-ui/core/styles/createTypography';
import clsx from 'clsx';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      textAlign: 'center',
      margin: 'auto',
      padding: `${theme.spacing(3)}px ${theme.spacing(5)}px`
    },
    text: {
      color: theme.palette.grey[700]
    },
    subtitle: {
      fontWeight: 400
    }
  })
);

interface EmptyResultSetMessageProps
  extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  message?: string;
  messageVariant?: TypographyVariant;
  variant?: 'search' | 'custom';
  subtitle?: string;
  subtitleVariant?: TypographyVariant;
}

/**
 * This component should be used for all Empty Result Set Messaging on app searches
 *
 * @param props.message Accepts String to use default message
 */
const EmptyResultSetMessage: React.FC<PropsWithChildren<EmptyResultSetMessageProps>> = ({
  className,
  message,
  messageVariant = 'h2',
  children,
  subtitle,
  subtitleVariant = 'h4',
  variant = 'search',
  ...rest
}) => {
  const classes = useStyles();
  if (!message) return null;
  const main =
    variant === 'search' && !subtitle ? `We couldn't find a match for "${message}"` : message;
  const sub = variant === 'search' && !subtitle ? 'Please try another search.' : subtitle;
  return (
    <div className={clsx(classes.root, className)} {...rest}>
      <Typography className={classes.text} variant={messageVariant} component="div">
        {main}
      </Typography>
      {sub && (
        <Typography
          className={clsx(classes.text, classes.subtitle)}
          variant={subtitleVariant}
          component="div"
        >
          {sub}
        </Typography>
      )}
      {children}
    </div>
  );
};

export default EmptyResultSetMessage;
